import { createBrowserRouter, Navigate } from 'react-router-dom'

import { appRoutes } from '@/constants'
import { AppLayout, DashboardLayout, PublicLayout } from '@/layouts'
import {
  HomePage,
  AllCarsPage,
  CarDetailsPage,
  CarModelShowcasePage,
  CarBrandShowcasePage,
  AllPartsPage,
  PartDetailsPage,
  DashboardRedirect,
  NotFoundPage,
  RouteErrorPage,
} from '@/pages'
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  VerificationPendingPage,
} from '@/features/auth/pages'
import {
  CustomerDashboardPage,
  BecomeSellerPage,
  BecomeDealerPage,
  ApprovalHistoryPage,
  CustomerOrdersPage,
} from '@/modules/customer'
import { MessagesPage } from '@/modules/chat/pages/messages-page'
import { ChooseSubscriptionPage } from '@/modules/customer/pages/Subscription/choose-subscription-page'
import {
  SellerDashboardPage,
  SellerBillingPage,
  SellerCarsInventoryPage,
  SellerCreateCarPage,
  SellerEditCarPage,
} from '@/modules/seller'
import {
  DealerDashboardPage,
  DealerBillingPage,
  DealerCarsInventoryPage,
  DealerCreateCarPage,
  DealerEditCarPage,
} from '@/modules/dealer'
import {
  AdminLayout,
  DashboardPage as AdminDashboardPage,
  UsersPage,
  SellerRequestsPage,
  DealerRequestsPage,
  SubscriptionsPage,
  PlansManagementPage,
  FormBuilderPage,
  WorkflowSettingsPage,
  AuditLogsPage,
  AdminBillingPage,
  PaymentsPage,
  SettingsPage,
  AdminPartsPage,
  AdminOrdersPage,
  CarsOverviewPage,
  BrandsPage,
  ModelsPage,
  GenerationsPage,
  VariantsPage,
  FeaturesPage,
  ListingsModerationPage,
  ModelRequestsPage,
} from '@/modules/admin'
import { ProtectedRoute } from './protected-route'
import { GuestRoute } from './guest-route'
import { RoleRoute } from './role-route'

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          {
            index: true,
            element: <HomePage />,
          },
          {
            path: appRoutes.cars,
            element: <AllCarsPage />,
          },
          {
            path: appRoutes.carDetails(':slug'),
            element: <CarDetailsPage />,
          },
          {
            path: appRoutes.carModelShowcase(':modelSlug'),
            element: <CarModelShowcasePage />,
          },
          {
            path: appRoutes.carBrandShowcase(':brandSlug'),
            element: <CarBrandShowcasePage />,
          },
          {
            path: appRoutes.parts,
            element: <AllPartsPage />,
          },
          {
            path: appRoutes.partDetails(':slug'),
            element: <PartDetailsPage />,
          },
          {
            path: appRoutes.notFound,
            element: <NotFoundPage />,
          },
        ],
      },

      {
        path: appRoutes.auth.login,
        element: (
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        ),
      },
      {
        path: appRoutes.auth.register,
        element: (
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        ),
      },
      {
        path: appRoutes.auth.forgotPassword,
        element: (
          <GuestRoute>
            <ForgotPasswordPage />
          </GuestRoute>
        ),
      },
      {
        path: appRoutes.auth.resetPassword,
        element: (
          <GuestRoute>
            <ResetPasswordPage />
          </GuestRoute>
        ),
      },
      {
        path: appRoutes.auth.verifyEmail,
        element: (
          <GuestRoute>
            <VerifyEmailPage />
          </GuestRoute>
        ),
      },
      {
        path: appRoutes.auth.verificationPending,
        element: (
          <GuestRoute>
            <VerificationPendingPage />
          </GuestRoute>
        ),
      },
      {
        path: appRoutes.auth.oauthFailure,
        element: (
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        ),
      },

      {
        path: appRoutes.dashboard.root,
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <>
                <DashboardRedirect />
                <CustomerDashboardPage />
              </>
            ),
          },
          {
            path: 'become-seller',
            element: <BecomeSellerPage />,
          },
          {
            path: 'become-dealer',
            element: <BecomeDealerPage />,
          },
          {
            path: 'approval-history',
            element: <ApprovalHistoryPage />,
          },
          {
            path: 'orders',
            element: <CustomerOrdersPage />,
          },
          {
            path: 'messages',
            element: <MessagesPage />,
          },
        ],
      },

      {
        path: '/customer',
        element: (
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'become-seller',
            element: <BecomeSellerPage />,
          },
          {
            path: 'become-dealer',
            element: <BecomeDealerPage />,
          },
          {
            path: 'approval-history',
            element: <ApprovalHistoryPage />,
          },
          {
            path: 'orders',
            element: <CustomerOrdersPage />,
          },
          {
            path: 'choose-subscription',
            element: <ChooseSubscriptionPage />,
          },
          {
            path: 'messages',
            element: <MessagesPage />,
          },
        ],
      },

      {
        path: appRoutes.seller.root,
        element: (
          <RoleRoute roles={['SELLER']}>
            <DashboardLayout />
          </RoleRoute>
        ),
        children: [
          { index: true, element: <SellerDashboardPage /> },
          { path: 'messages', element: <MessagesPage /> },
          { path: 'orders', element: <CustomerOrdersPage /> },
          { path: 'billing', element: <SellerBillingPage /> },
          { path: 'cars', element: <SellerCarsInventoryPage /> },
          { path: 'cars/new', element: <SellerCreateCarPage /> },
          { path: 'cars/:id/edit', element: <SellerEditCarPage /> },
        ],
      },

      {
        path: appRoutes.dealer.root,
        element: (
          <RoleRoute roles={['DEALER']}>
            <DashboardLayout />
          </RoleRoute>
        ),
        children: [
          { index: true, element: <DealerDashboardPage /> },
          { path: 'messages', element: <MessagesPage /> },
          { path: 'orders', element: <CustomerOrdersPage /> },
          { path: 'billing', element: <DealerBillingPage /> },
          { path: 'cars', element: <DealerCarsInventoryPage /> },
          { path: 'cars/new', element: <DealerCreateCarPage /> },
          { path: 'cars/:id/edit', element: <DealerEditCarPage /> },
        ],
      },

      {
        path: appRoutes.admin.root,
        element: (
          <RoleRoute roles={['ADMIN']}>
            <AdminLayout />
          </RoleRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to={appRoutes.admin.dashboard} replace />,
          },
          {
            path: 'dashboard',
            element: <AdminDashboardPage />,
          },
          {
            path: 'cars',
            children: [
              { index: true, element: <CarsOverviewPage /> },
              { path: 'brands', element: <BrandsPage /> },
              { path: 'models', element: <ModelsPage /> },
              { path: 'generations', element: <GenerationsPage /> },
              { path: 'variants', element: <VariantsPage /> },
              { path: 'features', element: <FeaturesPage /> },
              { path: 'listings', element: <ListingsModerationPage /> },
              { path: 'model-requests', element: <ModelRequestsPage /> },
            ],
          },
          {
            path: 'users',
            element: <UsersPage />,
          },
          {
            path: 'seller-requests',
            element: <SellerRequestsPage />,
          },
          {
            path: 'dealer-requests',
            element: <DealerRequestsPage />,
          },
          {
            path: 'subscriptions',
            element: <SubscriptionsPage />,
          },
          {
            path: 'billing',
            element: <AdminBillingPage />,
          },
          {
            path: 'plans-management',
            element: <PlansManagementPage />,
          },
          {
            path: 'form-builder',
            element: <FormBuilderPage />,
          },
          {
            path: 'workflow-settings',
            element: <WorkflowSettingsPage />,
          },
          {
            path: 'audit-logs',
            element: <AuditLogsPage />,
          },
          {
            path: 'payments',
            element: <PaymentsPage />,
          },
          {
            path: 'parts',
            element: <AdminPartsPage />,
          },
          {
            path: 'orders',
            element: <AdminOrdersPage />,
          },
          {
            path: 'settings',
            element: <SettingsPage />,
          },
        ],
      },

      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])